import React from "react";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import { Box, Card, CardContent, Grid, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import StripeClimateBadgeIframe from "../components/StripeClimateBadgeIframe";
import { APP_SITE_ID, COMPANY_LOGO_SRC, CONTACT_TYPE, resolveAppIconSrc, resolveContactPath, resolveAppSiteEntry } from "../../config/appSiteLinks";

/**
 * Shared “Contact us” block used on Support and Marketing (same copy as support.contact.*).
 * @param {function} t - translate from usePagesTranslation (respects ?lang= when passed from parent)
 */
export default function FooterContactUsCard({ t, appId }) {
    const appEntry = resolveAppSiteEntry(appId);
    const appIcon = resolveAppIconSrc(appEntry?.id);
    const contactTo = (appEntry && appEntry.id !== APP_SITE_ID.WEB)
        ? resolveContactPath({ type: CONTACT_TYPE.FEEDBACK, app: appEntry.id })
        : "/contact";
    return (
        <Card sx={{ boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="flex-start">
                    {/* Row 1 — logo | contact (stack on xs) */}
                    <Grid
                        size={{ xs: 12, sm: 6 }}
                        sx={{
                            display: "flex",
                            justifyContent: { xs: "center", sm: "flex-start" },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                width: "100%",
                                maxWidth: "100%",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                                <Box
                                    component="img"
                                    src={COMPANY_LOGO_SRC}
                                    alt={t("landing.title")}
                                    sx={{
                                        width: { xs: 80, sm: 96 },
                                        height: { xs: 80, sm: 96 },
                                        objectFit: "contain",
                                    }}
                                />
                                {appIcon ? (
                                    <Box
                                        component="img"
                                        src={appIcon}
                                        alt={appEntry.displayName}
                                        sx={{
                                            width: { xs: 72, sm: 88 },
                                            height: { xs: 72, sm: 88 },
                                            objectFit: "contain",
                                            borderRadius: "22%",
                                        }}
                                    />
                                ) : null}
                            </Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    mt: 1,
                                    textAlign: "center",
                                    lineHeight: 1.35,
                                    width: "100%",
                                    maxWidth: 280,
                                    fontWeight: 600,
                                }}
                            >
                                {appIcon
                                    ? `${t("landing.title")} · ${appEntry.displayName}`
                                    : "Puzzle Interact"}
                            </Typography>
                            <StripeClimateBadgeIframe sx={{ mt: 2 }} />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                            <ContactMailIcon sx={{ color: "#3BA666", fontSize: 28 }} />
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                {t("support.contact.title")}
                            </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {t("support.contact.intro")}
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, m: 0 }}>
                            <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                                <Typography component="span" sx={{ fontWeight: 700 }}>
                                    {t("support.contact.form-label")}
                                </Typography>{" "}
                                <Link
                                    component={RouterLink}
                                    to={contactTo}
                                    sx={{ color: "#3BA666", fontWeight: 600, textDecoration: "underline" }}
                                >
                                    {t("support.contact.form-link")}
                                </Link>
                            </Typography>
                            <Typography component="li" variant="body1">
                                <Typography component="span" sx={{ fontWeight: 700 }}>
                                    {t("support.contact.email-label")}
                                </Typography>{" "}
                                <Box
                                    component="a"
                                    href="mailto:support@puzzleinteract.com"
                                    sx={{ color: "#3BA666", fontWeight: 600, textDecoration: "underline" }}
                                >
                                    {t("support.contact.email-address")}
                                </Box>
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}
